import {
  Collapse,
  CollapseProps,
  IconButton,
  makeStyles,
  useTheme,
} from '@material-ui/core';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import clsx from 'clsx';
import {
  FC,
  MouseEventHandler,
  ReactNode,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { useElementSize } from '../../hooks';
import { WbCard, WbCardProps } from '../WbCard';

interface WbDisclosureProps extends WbCardProps {
  children?: ReactNode;
  renderDynamicContent?: (expanded: boolean) => ReactNode;
  headerInfo?: JSX.Element;
  footer?: JSX.Element;
  cardClassName?: string;
  expanded?: boolean;
  collapseProps?: CollapseProps;
  handleChange?: MouseEventHandler<HTMLButtonElement>;
  placeholderHeight?: string;
  titleId?: string;
  buttonProps?: React.ComponentProps<typeof IconButton>;
}

const useCollapseStyles = makeStyles(
  theme => ({
    root: {
      borderLeft: `1px solid ${theme.palette.divider}`,
      borderRight: `1px solid ${theme.palette.divider}`,
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    hidden: {
      border: 'none',
    },
  }),
  { name: 'WbDisclosureCollapse' },
);

const useStyles = makeStyles(theme => ({
  disclosureHeader: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: theme.spacing(2),
    width: '100%',
    height: '20px',
  },
  disclosureDetails: {
    backgroundColor: theme.palette.background.default,
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    padding: '8px 0px 0px 0px',
  },
  card: {
    background: theme.palette.background.default,
  },
  footer: {
    textAlign: 'end',
    padding: '8px',
    background: theme.palette.background.default,
  },
  iconContainer: {
    padding: 0,
    marginLeft: 10,
    transform: 'rotate(0deg)',
    '&$expanded': {
      transform: 'rotate(90deg)',
    },
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
  },
  icon: {
    fontSize: '24px',
    padding: '2px',
    color: theme.palette.secondary.main,
  },
  expanded: {},
}));

const CollapseDynamicContent = ({
  expanded,
  children,
  placeholderHeight,
  collapseProps,
  regionId,
  labelledBy,
}: {
  expanded: boolean;
  children: ReactNode;
  placeholderHeight?: string;
  collapseProps?: CollapseProps;
  regionId: string;
  labelledBy: string;
}) => {
  const contentRef = useRef<HTMLDivElement>(null);

  const collapseClasses = useCollapseStyles();

  const { clientHeight } = useElementSize(contentRef, 10);

  const [collapsedHeight, setCollapsedHeight] = useState(0);

  useEffect(() => {
    if (!expanded) setCollapsedHeight(clientHeight);
  }, [expanded, clientHeight]);

  return (
    <Collapse
      classes={collapseClasses}
      {...collapseProps}
      collapsedSize={
        !collapsedHeight && placeholderHeight
          ? placeholderHeight
          : collapsedHeight
      }
      in={expanded}
    >
      <div
        ref={contentRef}
        id={regionId}
        role="region"
        aria-labelledby={labelledBy}
      >
        {children}
      </div>
    </Collapse>
  );
};

export const WbDisclosure: FC<WbDisclosureProps> = ({
  title,
  children,
  footer,
  headerInfo,
  cardClassName,
  expanded: parentExpanded,
  handleChange: parentHandleChange,
  headerStyle,
  cardStyle,
  collapseProps,
  renderDynamicContent,
  placeholderHeight,
  titleId,
  ...props
}) => {
  const classes = useStyles();

  const theme = useTheme();

  const collapseClasses = useCollapseStyles();

  const [innerExpanded, setInnerExpanded] = useState(false);

  const innerOnChange = () => {
    setInnerExpanded(old => !old);
  };

  const expanded = parentExpanded ?? innerExpanded;
  const handleChange = parentHandleChange ?? innerOnChange;

  const disclosureButtonId = useId();
  const disclosureRegionId = useId();
  const fallbackTitleId = useId();

  const usedId = titleId ?? fallbackTitleId;

  return (
    <WbCard
      footer={footer}
      leading={
        <IconButton
          onClick={handleChange ?? innerOnChange}
          className={clsx(classes.iconContainer, expanded && classes.expanded)}
          size="small"
          aria-labelledby={usedId}
          aria-expanded={expanded}
          aria-controls={disclosureRegionId}
          id={disclosureButtonId}
        >
          <KeyboardArrowRightIcon color="primary" className={classes.icon} />
        </IconButton>
      }
      titleId={usedId}
      title={title}
      icon={headerInfo}
      headerStyle={{
        background: theme.palette.grey[50],
        // Define borders separately to avoid override from outside without bugs
        borderTop: `1px solid ${theme.palette.divider}`,
        borderLeft: `1px solid ${theme.palette.divider}`,
        borderRight: `1px solid ${theme.palette.divider}`,
        borderBottom: `1px solid ${theme.palette.divider}`,
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        ...headerStyle,
      }}
      headerContentStyle={{
        height: 'auto',
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
      }}
      cardStyle={{
        boxShadow: 'none',
        ...cardStyle,
      }}
      {...props}
    >
      {renderDynamicContent ? (
        <CollapseDynamicContent
          placeholderHeight={placeholderHeight}
          expanded={expanded}
          regionId={disclosureRegionId}
          labelledBy={disclosureButtonId}
        >
          {renderDynamicContent(expanded)}
        </CollapseDynamicContent>
      ) : (
        <Collapse classes={collapseClasses} {...collapseProps} in={expanded}>
          <div
            id={disclosureRegionId}
            role="region"
            aria-labelledby={disclosureButtonId}
          >
            {children}
          </div>
        </Collapse>
      )}
    </WbCard>
  );
};
